import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

export type AppLang = 'es' | 'en';

/**
 * Idioma de la app, centralizado y PERSISTIDO. Unifica el cambio de idioma para que la
 * elección sobreviva recargas y navegación entre vistas. `init()` aplica el idioma
 * guardado al arrancar para que la primera vista ya respete la elección del usuario.
 */
@Injectable({providedIn: 'root'})
export class LanguageService {
  private translate = inject(TranslateService);

  private readonly STORAGE_KEY = 'app-lang';
  private readonly fallback: AppLang = 'es';
  readonly supported: AppLang[] = ['es', 'en'];

  /** Aplica el idioma persistido (o el default) al iniciar la app. */
  init(): void {
    this.translate.use(this.resolveInitial());
  }

  /** Idioma actual ('es' | 'en'). */
  current(): AppLang {
    const cur = this.translate.currentLang as AppLang;
    return this.supported.includes(cur) ? cur : this.resolveInitial();
  }

  /** Cambia el idioma y lo persiste. */
  set(lang: AppLang): void {
    if (!this.supported.includes(lang)) return;
    try {
      localStorage.setItem(this.STORAGE_KEY, lang);
    } catch { /* storage lleno → solo en memoria */ }
    this.translate.use(lang);
  }

  /** Alterna entre los idiomas soportados. */
  toggle(): void {
    this.set(this.current() === 'es' ? 'en' : 'es');
  }

  private resolveInitial(): AppLang {
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(this.STORAGE_KEY);
    } catch { /* acceso a storage bloqueado */ }
    return saved && this.supported.includes(saved as AppLang) ? (saved as AppLang) : this.fallback;
  }
}
